import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  stock: string;
  category_id: string;
  active: boolean;
  colors: string[];
  sizes: string[];
  mainImage: File | null;
  colorImages: { [color: string]: File };
}

interface ImageStructure {
  main: string;
  colors?: { [color: string]: string };
}

function ProductForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error, warning } = useNotification();
  
  const productId = searchParams.get('edit');
  const isNew = searchParams.get('new') === 'true';
  const isEdit = !!productId;

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: '',
    stock: '',
    category_id: '',
    active: true,
    colors: [],
    sizes: [],
    mainImage: null,
    colorImages: {},
  });
  const [mainImagePreview, setMainImagePreview] = useState<string>('');
  const [colorImagePreviews, setColorImagePreviews] = useState<{ [color: string]: string }>({});
  const [availableColors] = useState<string[]>(['Branco', 'Preto', 'Azul', 'Vermelho', 'Verde', 'Amarelo', 'Rosa', 'Cinza', 'Marrom', 'Bege']);
  const [availableSizes] = useState<string[]>(['PP', 'P', 'M', 'G', 'GG', 'XG']);

  useEffect(() => {
    loadCategories();
    if (isEdit && productId) {
      loadProduct(parseInt(productId));
    }
  }, [isEdit, productId]);

  const loadCategories = async () => {
    try {
      const categoriesData = await AdminAPI.get('categories') as any[];
      setCategories(categoriesData);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  const loadProduct = async (id: number) => {
    try {
      const product = await AdminAPI.get(`products/${id}`) as any;
      
      // Parse imagens (pode ser array simples ou objeto com main e colors)
      let mainImageUrl = '';
      let colorImagesObj: { [color: string]: string } = {};
      
      if (product.images) {
        let imagesData: any = product.images;
        if (typeof imagesData === 'string') {
          try {
            imagesData = JSON.parse(imagesData);
          } catch (e) {
            imagesData = [];
          }
        }
        
        if (typeof imagesData === 'object' && !Array.isArray(imagesData)) {
          // Estrutura nova: { main: "...", colors: { "Branco": "..." } }
          mainImageUrl = imagesData.main || '';
          colorImagesObj = imagesData.colors || {};
        } else if (Array.isArray(imagesData) && imagesData.length > 0) {
          // Estrutura antiga: array simples - usar primeira como principal
          mainImageUrl = imagesData[0] || '';
        }
      }
      
      // Parse cores e tamanhos
      let colors = [];
      let sizes = [];
      
      if (product.colors) {
        if (typeof product.colors === 'string') {
          try {
            colors = JSON.parse(product.colors);
          } catch (e) {
            colors = [];
          }
        } else if (Array.isArray(product.colors)) {
          colors = product.colors;
        }
      }
      
      if (product.sizes) {
        if (typeof product.sizes === 'string') {
          try {
            sizes = JSON.parse(product.sizes);
          } catch (e) {
            sizes = [];
          }
        } else if (Array.isArray(product.sizes)) {
          sizes = product.sizes;
        }
      }
      
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: String(product.price || ''),
        stock: String(product.stock || ''),
        category_id: String(product.category_id || ''),
        active: product.active !== false,
        colors: colors,
        sizes: sizes,
        mainImage: null,
        colorImages: {},
      });
      
      setMainImagePreview(mainImageUrl);
      setColorImagePreviews(colorImagesObj);
    } catch (err) {
      console.error('Erro ao carregar produto:', err);
      error('Erro ao carregar produto');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleColorToggle = (color: string) => {
    setFormData((prev) => {
      const colors = prev.colors.includes(color)
        ? prev.colors.filter(c => c !== color)
        : [...prev.colors, color];
      return { ...prev, colors };
    });
  };

  const handleSizeToggle = (size: string) => {
    setFormData((prev) => {
      const sizes = prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size];
      return { ...prev, sizes };
    });
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, mainImage: file }));
      setMainImagePreview(URL.createObjectURL(file));
      e.target.value = '';
    }
  };

  const handleColorImageChange = (color: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        colorImages: { ...prev.colorImages, [color]: file }
      }));
      setColorImagePreviews((prev) => ({
        ...prev,
        [color]: URL.createObjectURL(file)
      }));
      e.target.value = '';
    }
  };

  const removeMainImage = () => {
    if (mainImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(mainImagePreview);
    }
    setFormData((prev) => ({ ...prev, mainImage: null }));
    setMainImagePreview('');
  };

  const removeColorImage = (color: string) => {
    const preview = colorImagePreviews[color];
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setFormData((prev) => {
      const newColorImages = { ...prev.colorImages };
      delete newColorImages[color];
      return { ...prev, colorImages: newColorImages };
    });
    setColorImagePreviews((prev) => {
      const newPreviews = { ...prev };
      delete newPreviews[color];
      return newPreviews;
    });
  };

  // Função para converter File para base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Converter imagem principal para base64
      let mainImageBase64 = '';
      if (formData.mainImage) {
        try {
          mainImageBase64 = await fileToBase64(formData.mainImage);
        } catch (err) {
          console.error('Erro ao converter imagem principal:', err);
          warning('Erro ao processar imagem principal');
        }
      } else if (mainImagePreview && !mainImagePreview.startsWith('blob:')) {
        // Manter imagem existente se não foi substituída
        mainImageBase64 = mainImagePreview;
      }

      // Converter imagens por cor para base64
      const colorImagesBase64: { [color: string]: string } = {};
      
      // Manter imagens existentes que não foram removidas
      Object.keys(colorImagePreviews).forEach((color) => {
        const preview = colorImagePreviews[color];
        if (!preview.startsWith('blob:') && preview) {
          // Imagem existente (base64 ou URL)
          colorImagesBase64[color] = preview;
        }
      });
      
      // Adicionar novas imagens por cor
      for (const [color, file] of Object.entries(formData.colorImages)) {
        try {
          const base64 = await fileToBase64(file);
          colorImagesBase64[color] = base64;
        } catch (err) {
          console.error(`Erro ao converter imagem da cor ${color}:`, err);
          warning(`Erro ao processar imagem da cor ${color}`);
        }
      }

      // Estrutura de imagens: { main: "...", colors: { "Branco": "..." } }
      const imagesStructure: ImageStructure = {
        main: mainImageBase64,
      };
      
      if (Object.keys(colorImagesBase64).length > 0) {
        imagesStructure.colors = colorImagesBase64;
      }

      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        category_id: parseInt(formData.category_id),
        active: formData.active,
        colors: formData.colors,
        sizes: formData.sizes,
        images: imagesStructure,
      };

      let result;
      if (isEdit && productId) {
        // Atualizar produto existente
        result = await AdminAPI.put(`products/${productId}`, productData);
        success('Produto atualizado com sucesso!');
      } else {
        // Criar novo produto
        result = await AdminAPI.post('products', productData);
        success('Produto criado com sucesso!');
      }

      setTimeout(() => {
        navigate('/admin/produtos');
      }, 1000);
    } catch (err: any) {
      console.error('Erro ao salvar produto:', err);
      error(err.message || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-content">
      <form className="product-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="name">
            Nome do Produto *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder="Ex: Camiseta Premium"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="description">
            Descrição
          </label>
          <textarea
            id="description"
            name="description"
            className="form-textarea"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Descreva o produto..."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="price">
              Preço (R$) *
            </label>
            <input
              type="number"
              step="0.01"
              id="price"
              name="price"
              className="form-input"
              value={formData.price}
              onChange={handleInputChange}
              required
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="stock">
              Estoque *
            </label>
            <input
              type="number"
              id="stock"
              name="stock"
              className="form-input"
              value={formData.stock}
              onChange={handleInputChange}
              required
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="category_id">
            Categoria *
          </label>
          <select
            id="category_id"
            name="category_id"
            className="form-select"
            value={formData.category_id}
            onChange={handleInputChange}
            required
          >
            <option value="">Selecione uma categoria</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Cores */}
        <div className="form-group">
          <label className="form-label">
            Cores Disponíveis
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
            {availableColors.map((color) => (
              <label
                key={color}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  border: `1.5px solid ${formData.colors.includes(color) ? 'var(--secondary)' : 'var(--border)'}`,
                  borderRadius: '8px',
                  background: formData.colors.includes(color) ? 'rgba(52, 152, 219, 0.1)' : 'var(--surface)',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.colors.includes(color)}
                  onChange={() => handleColorToggle(color)}
                  style={{ cursor: 'pointer' }}
                />
                <span>{color}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Tamanhos */}
        <div className="form-group">
          <label className="form-label">
            Tamanhos Disponíveis
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
            {availableSizes.map((size) => (
              <label
                key={size}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  border: `1.5px solid ${formData.sizes.includes(size) ? 'var(--secondary)' : 'var(--border)'}`,
                  borderRadius: '8px',
                  background: formData.sizes.includes(size) ? 'rgba(52, 152, 219, 0.1)' : 'var(--surface)',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.sizes.includes(size)}
                  onChange={() => handleSizeToggle(size)}
                  style={{ cursor: 'pointer' }}
                />
                <span>{size}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Imagem Principal */}
        <div className="form-group">
          <label className="form-label">
            Imagem Principal *
          </label>
          <div className="image-upload">
            <input
              type="file"
              id="main-image"
              accept="image/*"
              onChange={handleMainImageChange}
            />
            <label htmlFor="main-image" style={{ cursor: 'pointer' }}>
              <i className="fas fa-camera" style={{ fontSize: '32px', color: 'var(--text-secondary)', marginBottom: '8px' }}></i>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Clique para adicionar imagem principal
              </div>
            </label>
          </div>

          {mainImagePreview && (
            <div className="image-preview" style={{ marginTop: '12px' }}>
              <div className="image-preview-item">
                <img src={mainImagePreview} alt="Imagem principal" />
                <button
                  type="button"
                  className="remove-image"
                  onClick={removeMainImage}
                  aria-label="Remover imagem principal"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Imagens por Cor */}
        {formData.colors.length > 0 && (
          <div className="form-group">
            <label className="form-label">
              Imagens por Cor (Opcional)
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
              {formData.colors.map((color) => (
                <div key={color} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>Imagem para {color}</span>
                    {colorImagePreviews[color] && (
                      <button
                        type="button"
                        onClick={() => removeColorImage(color)}
                        style={{
                          background: 'var(--accent)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        Remover
                      </button>
                    )}
                  </div>
                  <div className="image-upload" style={{ minHeight: '80px' }}>
                    <input
                      type="file"
                      id={`color-image-${color}`}
                      accept="image/*"
                      onChange={(e) => handleColorImageChange(color, e)}
                    />
                    <label htmlFor={`color-image-${color}`} style={{ cursor: 'pointer' }}>
                      {colorImagePreviews[color] ? (
                        <img
                          src={colorImagePreviews[color]}
                          alt={`Imagem ${color}`}
                          style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '8px' }}
                        />
                      ) : (
                        <>
                          <i className="fas fa-camera" style={{ fontSize: '24px', color: 'var(--text-secondary)', marginBottom: '4px' }}></i>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            Adicionar imagem para {color}
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-checkbox">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleInputChange}
            />
            <span>Produto ativo</span>
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/produtos')}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !mainImagePreview}
          >
            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProductForm;
